'use client';

/**
 * WhatsAppSendSection - WhatsApp compose for Chat tab
 *
 * Renders contact selector and message compose area.
 * Schema-driven: rendered when CHAT_SCHEMA includes type='whatsapp_send'.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot
 */

import { useState } from 'react';
import { toast } from 'sonner';
import type { ContactSummary, WhatsAppMediaAttachment } from '@/lib/api/types';
import { ContactSelector } from '../contact-selector';
import { WhatsAppComposeWithContactSelector } from '../whatsapp-compose';

interface WhatsAppSendSectionProps {
  contacts: ContactSummary[];
  contactsLoading?: boolean;
  onSend: (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => Promise<{ success: boolean; error?: string }>;
  isSending?: boolean;
}

export function WhatsAppSendSection({
  contacts,
  contactsLoading = false,
  onSend,
  isSending = false,
}: WhatsAppSendSectionProps) {
  const [selectedContact, setSelectedContact] = useState<ContactSummary | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<WhatsAppMediaAttachment[]>([]);

  const handleSend = async () => {
    const phoneToUse = showManualEntry ? manualPhone : selectedContact?.phone;
    if (!phoneToUse) {
      toast.error('No phone number', { description: 'Please select a contact or enter a phone number' });
      return;
    }

    const result = await onSend(phoneToUse, message, attachments.length > 0 ? attachments : undefined);
    if (result.success) {
      toast.success('WhatsApp sent', { description: `Sent to ${selectedContact?.name || phoneToUse}` });
      setMessage('');
      setAttachments([]);
    } else {
      toast.error('Failed to send', { description: result.error || 'Unknown error' });
    }
  };

  const canSend =
    !!((showManualEntry && manualPhone.trim()) || (!showManualEntry && selectedContact?.phone)) &&
    (!!message.trim() || attachments.length > 0) &&
    !isSending;

  return (
    <WhatsAppComposeWithContactSelector
      message={message}
      onMessageChange={setMessage}
      onSend={handleSend}
      canSend={canSend}
      isSending={isSending}
      selectedContactName={selectedContact?.name}
      showManualEntry={showManualEntry}
      manualPhone={manualPhone}
      onManualPhoneChange={setManualPhone}
      onCancelManualEntry={() => {
        setShowManualEntry(false);
        setManualPhone('');
      }}
      warning={selectedContact && !selectedContact.phone ? 'This contact has no phone number' : undefined}
      attachments={attachments}
      onAttachmentsChange={setAttachments}
    >
      <ContactSelector
        contacts={contacts}
        selectedContact={selectedContact}
        onSelect={setSelectedContact}
        onManualEntry={() => setShowManualEntry(true)}
        isLoading={contactsLoading}
      />
    </WhatsAppComposeWithContactSelector>
  );
}
